import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, FileText, 
  Ship, Plane, Truck, Globe, RefreshCw, Package,
  DollarSign, CheckCircle, Clock, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MetricsDashboardPage = () => {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const isSpanish = i18n.language === 'es';
  
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    if (isAuthenticated ) {
      fetchMetrics();
    }
  }, [isAuthenticated, token]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const headers = {  };
      
      const [metricsRes, trendRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/metrics/overview`, { headers }),
        fetch(`${API_URL}/api/admin/metrics/quotes-trend?days=30`, { headers })
      ]);
      
      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
      } else {
        toast.error(isSpanish ? 'Error al cargar métricas' : 'Failed to load metrics');
      }
      
      if (trendRes.ok) {
        const data = await trendRes.json();
        setTrend(data.trend || []);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Auth checks
  if (authLoading) {
    return (
      <div className="min-h-screen bg-steel-50 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Calculate trend percentage
  const quoteTrend = metrics?.quotes?.this_month && metrics?.quotes?.last_month
    ? ((metrics.quotes.this_month - metrics.quotes.last_month) / (metrics.quotes.last_month || 1) * 100).toFixed(1)
    : 0;
  const isPositiveTrend = parseFloat(quoteTrend) >= 0;

  // Get max value for chart scaling
  const maxTrendValue = Math.max(...trend.map(t => t.count), 1);

  return (
    <div className="min-h-screen bg-steel-50 pt-24" data-testid="metrics-dashboard-page">
      {/* Header */}
      <section className="bg-navy-900 py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
                {isSpanish ? 'Dashboard de Métricas y KPIs' : 'Metrics & KPIs Dashboard'}
              </h1>
              <p className="text-steel-300">
                {isSpanish 
                  ? 'Análisis de rendimiento y métricas clave del negocio'
                  : 'Business performance analysis and key metrics'}
              </p>
            </div>
            <Button 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
              onClick={fetchMetrics}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {isSpanish ? 'Actualizar' : 'Refresh'}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="container-custom">
          {loading && !metrics ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
              <p className="text-steel-600">{isSpanish ? 'Cargando métricas...' : 'Loading metrics...'}</p>
            </div>
          ) : metrics ? (
            <div className="space-y-8">
              {/* Main KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total Quotes */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-blue-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <FileText className="w-8 h-8 text-white/80" />
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        isPositiveTrend ? 'bg-green-400/30 text-green-100' : 'bg-red-400/30 text-red-100'
                      }`}>
                        {isPositiveTrend ? '+' : ''}{quoteTrend}%
                      </span>
                    </div>
                    <p className="text-3xl font-bold text-white">{metrics.quotes.total}</p>
                    <p className="text-sm text-white/70">{isSpanish ? 'Cotizaciones Totales' : 'Total Quotes'}</p>
                  </CardContent>
                </Card>

                {/* Active Shipments */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <Ship className="w-8 h-8 text-white/80" />
                    </div>
                    <p className="text-3xl font-bold text-white">{metrics.shipments.active}</p>
                    <p className="text-sm text-white/70">{isSpanish ? 'Envíos Activos' : 'Active Shipments'}</p>
                  </CardContent>
                </Card>

                {/* Total Clients */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-green-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <Users className="w-8 h-8 text-white/80" />
                    </div>
                    <p className="text-3xl font-bold text-white">{metrics.users.clients}</p>
                    <p className="text-sm text-white/70">{isSpanish ? 'Clientes Registrados' : 'Registered Clients'}</p>
                  </CardContent>
                </Card>

                {/* Conversion Rate */}
                <Card className="border-0 shadow-lg bg-gradient-to-br from-gold to-yellow-600">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="w-8 h-8 text-white/80" />
                    </div>
                    <p className="text-3xl font-bold text-white">{metrics.quotes.conversion_rate}%</p>
                    <p className="text-sm text-white/70">{isSpanish ? 'Tasa de Conversión' : 'Conversion Rate'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quote Status & Trend Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Quote Status Breakdown */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-gold" />
                      {isSpanish ? 'Estado de Cotizaciones' : 'Quote Status'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[
                        { key: 'pending', label: isSpanish ? 'Pendientes' : 'Pending', color: 'bg-yellow-500', icon: Clock },
                        { key: 'reviewing', label: isSpanish ? 'En Revisión' : 'Reviewing', color: 'bg-blue-500', icon: AlertCircle },
                        { key: 'quoted', label: isSpanish ? 'Cotizadas' : 'Quoted', color: 'bg-purple-500', icon: FileText },
                        { key: 'accepted', label: isSpanish ? 'Aceptadas' : 'Accepted', color: 'bg-green-500', icon: CheckCircle },
                        { key: 'rejected', label: isSpanish ? 'Rechazadas' : 'Rejected', color: 'bg-red-500', icon: XCircle }
                      ].map(status => {
                        const count = metrics.quotes.status_breakdown[status.key] || 0;
                        const percentage = metrics.quotes.total > 0 ? (count / metrics.quotes.total * 100) : 0;
                        const Icon = status.icon;
                        
                        return (
                          <div key={status.key} className="flex items-center gap-3">
                            <Icon className={`w-4 h-4 ${status.color.replace('bg-', 'text-')}`} />
                            <span className="text-sm text-steel-600 w-24">{status.label}</span>
                            <div className="flex-1 h-3 bg-steel-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${status.color} transition-all duration-500`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-navy-800 w-12 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Transport Mode Breakdown */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-gold" />
                      {isSpanish ? 'Modos de Transporte' : 'Transport Modes'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'air', label: isSpanish ? 'Aéreo' : 'Air', icon: Plane, color: 'text-blue-500 bg-blue-50' },
                        { key: 'ocean', label: isSpanish ? 'Marítimo' : 'Ocean', icon: Ship, color: 'text-cyan-600 bg-cyan-50' },
                        { key: 'road', label: isSpanish ? 'Terrestre' : 'Road', icon: Truck, color: 'text-green-500 bg-green-50' },
                        { key: 'multimodal', label: 'Multimodal', icon: Globe, color: 'text-purple-500 bg-purple-50' }
                      ].map(mode => {
                        const count = metrics.quotes.transport_modes[mode.key] || 0;
                        const Icon = mode.icon;
                        
                        return (
                          <div key={mode.key} className={`p-4 rounded-lg ${mode.color.split(' ')[1]}`}>
                            <div className="flex items-center gap-3">
                              <Icon className={`w-8 h-8 ${mode.color.split(' ')[0]}`} />
                              <div>
                                <p className="text-2xl font-bold text-navy-800">{count}</p>
                                <p className="text-sm text-steel-600">{mode.label}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Trend Chart */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-gold" />
                    {isSpanish ? 'Tendencia de Cotizaciones (últimos 30 días)' : 'Quote Trend (last 30 days)'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48 flex items-end gap-1">
                    {trend.map((day, index) => {
                      const height = (day.count / maxTrendValue) * 100;
                      return (
                        <div 
                          key={day.date} 
                          className="flex-1 group relative"
                          data-testid={`trend-bar-${index}`}
                        >
                          <div 
                            className="w-full bg-gradient-to-t from-gold to-yellow-400 rounded-t transition-all hover:from-gold hover:to-yellow-300"
                            style={{ height: `${Math.max(height, 4)}%` }}
                          />
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-navy-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                            {day.date}: {day.count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-steel-500">
                    <span>{trend[0]?.date}</span>
                    <span>{trend[trend.length - 1]?.date}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Geography Row */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Top Origins */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gold" />
                      {isSpanish ? 'Principales Países de Origen' : 'Top Origin Countries'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {metrics.geography.top_origins.length > 0 ? (
                      <div className="space-y-3">
                        {metrics.geography.top_origins.map((origin, index) => (
                          <div key={origin.country} className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <span className="flex-1 text-navy-800">{origin.country}</span>
                            <span className="text-steel-600 font-medium">{origin.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-steel-500 text-center py-4">{isSpanish ? 'Sin datos' : 'No data'}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Top Destinations */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-gold" />
                      {isSpanish ? 'Principales Países de Destino' : 'Top Destination Countries'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {metrics.geography.top_destinations.length > 0 ? (
                      <div className="space-y-3">
                        {metrics.geography.top_destinations.map((dest, index) => (
                          <div key={dest.country} className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center justify-center">
                              {index + 1}
                            </span>
                            <span className="flex-1 text-navy-800">{dest.country}</span>
                            <span className="text-steel-600 font-medium">{dest.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-steel-500 text-center py-4">{isSpanish ? 'Sin datos' : 'No data'}</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* System Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4 text-center">
                    <Package className="w-8 h-8 text-steel-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-navy-800">{metrics.catalog.equipment}</p>
                    <p className="text-sm text-steel-500">{isSpanish ? 'Equipos' : 'Equipment'}</p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4 text-center">
                    <Globe className="w-8 h-8 text-steel-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-navy-800">{metrics.catalog.routes}</p>
                    <p className="text-sm text-steel-500">{isSpanish ? 'Rutas Tarificadas' : 'Rated Routes'}</p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-8 h-8 text-steel-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-navy-800">{metrics.catalog.surcharges}</p>
                    <p className="text-sm text-steel-500">{isSpanish ? 'Recargos' : 'Surcharges'}</p>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-4 text-center">
                    <Users className="w-8 h-8 text-steel-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-navy-800">{metrics.users.total}</p>
                    <p className="text-sm text-steel-500">{isSpanish ? 'Usuarios Totales' : 'Total Users'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Last Updated */}
              <div className="text-center text-sm text-steel-500">
                {isSpanish ? 'Actualizado:' : 'Last updated:'} {new Date(metrics.generated_at).toLocaleString()}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-steel-300 mx-auto mb-4" />
              <p className="text-steel-600">{isSpanish ? 'No se pudieron cargar las métricas' : 'Could not load metrics'}</p>
              <Button onClick={fetchMetrics} className="mt-4">
                {isSpanish ? 'Reintentar' : 'Retry'}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MetricsDashboardPage;
